import * as $ from "jquery";
import * as Backbone from "backbone";
import * as Marionette from "backbone.marionette";
import * as _ from "underscore";
import {on, Options} from "../utils/decorators";


@Options({
    template: false,
    el: ".js-wheel-desktop-wrap",
    ui: {
        navTabsList: ".js-nav-tabs",
        defaultTab: ".js-default-tab",
        curriculumLinks: ".js-curriculum-wheel-link",
        curriculumDial: ".js-curriculum-dial"
    }
})

export default class ViewController extends Marionette.View<Backbone.Model>{

    constructor() {
        super();
    }

    // BEHAVIOR:
    // =====================================
    // on tab hover: activate current tab
    // on tab leave: revert to default tab
    // fix fast hovers: delay tab activation for 200ms

    // -------------------------------------
    // CONSTANTS
    // -------------------------------------

    public TAB_ACTIVATION_TIMER_ID;
    public CURRENT_TOTAL_ANGLE = 0;
    public $CURRENT_TAB;

    // -------------------------------------
    // EVENTS
    // -------------------------------------

    @on("mouseenter @ui.curriculumLinks")
    onMouseEnterCurriculumLink(JQueryEventObject: any) {
        const $this = $(JQueryEventObject.currentTarget);
        this.startActivateTabTimer($this);
    }

    @on("mouseleave @ui.curriculumLinks")
    onMouseLeaveCurriculumLink(JQueryEventObject: any) {
        // If mouse leave a link, activate invisible
        // default tab content
        const $defaultTab = this.ui.defaultTab;
        this.startActivateTabTimer($defaultTab);
    }

    // -------------------------------------
    // FUNCTIONS
    // -------------------------------------

    onRender() {
        console.log("Curriculum wheel viewcontroller rendered");
        this.setActiveTab(this.ui.defaultTab);
        this.activateTab(this.ui.defaultTab);
    }

    activateTab($tabToActivate: any) {
        const degreesToRotate = this.getDegreesToRotate(this.$CURRENT_TAB, $tabToActivate);
        this.CURRENT_TOTAL_ANGLE += degreesToRotate;
        // console.log(`wants to rotate by ${degreesToRotate} degrees`);
        // console.log(`total angle will be ${this.CURRENT_TOTAL_ANGLE} degrees`);

        if ($tabToActivate !== this.ui.defaultTab) {
            // Spin the dial to point at this link, and scale it onscreen
            this.ui.curriculumDial.css({
                "transform": `translate(-50%, -50%) rotateZ(${this.CURRENT_TOTAL_ANGLE}deg) scale(1)`
            });
        } else {
            // Special case for default tab, because it scales dial off-screen
            this.ui.curriculumDial.css({
                "transform": `translate(-50%, -50%) rotateZ(${this.CURRENT_TOTAL_ANGLE}deg) scale(1.3)`
            });
        }

        // Use Bootstrap show tab function to reveal center content
        $tabToActivate.tab("show");
        this.setActiveTab($tabToActivate);
    }

    startActivateTabTimer($tabToActivate: any) {

        // If a tab had been set to activate, but another is
        // triggered before activation, clear previous countdown.
        clearTimeout(this.TAB_ACTIVATION_TIMER_ID);

        // Create small delay for tab activation, to account
        // for fast hovers. e.g. only want `setActiveTab()`
        // to trigger 1x if there are several fast hovers
        this.TAB_ACTIVATION_TIMER_ID = setTimeout(() => {
            this.activateTab($tabToActivate);
        }, 200);
    }

    clearActivateTabTimer() {
        clearTimeout(this.TAB_ACTIVATION_TIMER_ID);
    }

    setActiveTab($activeTab) {
        this.$CURRENT_TAB = $activeTab;
        console.log(`current tab is now ${this.$CURRENT_TAB.data("dial-rotate")}`);
    }

    getRadiansToRotate($currentTab: any, $tabToActivate: any) {
        const currentTabPositionInRadians = $currentTab.data("degrees") * Math.PI / 180;
        const tabToActivatePositionInRadians = $tabToActivate.data("degrees") * Math.PI / 180;

        // Find which difference is LESS -> forward or back (*** CONTINUE HERE ***)
        const radiansToRotate = tabToActivatePositionInRadians - currentTabPositionInRadians;

        return radiansToRotate;
    }

    getDegreesToRotate($currentTab: any, $tabToActivate: any) {
        const currentTabPositionInDegrees = $currentTab.data("degrees");
        let nextTabPositionInDegrees = $tabToActivate.data("degrees");

        // Check to see if going from quadrant 4 to quadrant 1
        const nextTabQuadrant = this.getQuadrantLocation($tabToActivate);
        const currentTabQuadrant = this.getQuadrantLocation(this.$CURRENT_TAB);

        if (currentTabQuadrant === 4 && nextTabQuadrant === 1) {
            console.log("going from 4 to 1");

            // If going from 4 to 1, you're passing 0deg. Need to
            // keep increasing degree count, if you want it to move
            // clockwise
            nextTabPositionInDegrees += 360;
        }

        // TODO: determine why this is necessary
        // Think about starting from 0 and going 1 stop CCW
        if (currentTabQuadrant === 1 && nextTabQuadrant === 4) {
            nextTabPositionInDegrees = -1 * (360 - nextTabPositionInDegrees);
        }

        let degreesToRotate = nextTabPositionInDegrees - currentTabPositionInDegrees;

        // Insure you're going the SHORTEST distance. ONE way will be
        // <= to 180 degrees, since you're on a circle. If it's trying
        // to go the long way, reverse direction.
        if (Math.abs(degreesToRotate) >= 180) {
            const shorterAbsoluteDistanceInDegrees = 360 - Math.abs(degreesToRotate)

            // Tracking logs - worth keeping for math clarity
            // console.log("trying to go > 180 degrees");
            // console.log(`degreesToRotate = ${nextTabPositionInDegrees} - ${currentTabPositionInDegrees}`);
            // console.log(`degreesToRotate = ${degreesToRotate}`);
            // console.log(`shorter distance is ${shorterAbsoluteDistanceInDegrees}`);

            // If CCW
            if (degreesToRotate >= 0) {
                degreesToRotate = -1 * shorterAbsoluteDistanceInDegrees;
            } else {
                degreesToRotate = shorterAbsoluteDistanceInDegrees;
            }
        }

        // NOTE: Always ADDING the following value to CURRENT_TOTAL_ANGLE
        return degreesToRotate;
    }

    getQuadrantLocation($targetTab: any) {
        const targetTabPositionInDegrees = $targetTab.data("degrees");
        let quadrantNumber;

        if ((targetTabPositionInDegrees >= 0) && (targetTabPositionInDegrees < 90)) {
            quadrantNumber = 1;
        } else if ((targetTabPositionInDegrees >= 90) && (targetTabPositionInDegrees < 180)) {
            quadrantNumber = 2;
        } else if ((targetTabPositionInDegrees >= 180) && (targetTabPositionInDegrees < 270)) {
            quadrantNumber = 3;
        } else if ((targetTabPositionInDegrees >= 270) && (targetTabPositionInDegrees < 360)) {
            quadrantNumber = 4;
        }

        return quadrantNumber;
    }
}



// WEBPACK FOOTER //
// ./django/project/@static/@modules/curriculum-wheel/controller.ts